import { supabase } from '../supabaseClient';

// Test date configuration
export const USE_TEST_DATE = false; // Toggle to true/false for testing
export const TEST_DATE = '2024-01-22'; // Set test date YYYY-MM-DD
export const TIMEZONE = 'America/Mexico_City'; // Match Supabase timezone

// Function to get the current date (either real or test)
export const getCurrentDate = () => {
  if (USE_TEST_DATE) {
    console.log('Current date being used:', TEST_DATE);
    console.log('Using test date:', USE_TEST_DATE);
    return TEST_DATE;
  }

  // Get current date in Mexico City timezone
  const date = new Date();
  const mexicoDate = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);

  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = mexicoDate.split('/');
  const formattedDate = `${year}-${month}-${day}`;

  console.log('Current date being used:', formattedDate);
  console.log('Using test date:', USE_TEST_DATE);
  return formattedDate;
};

// Function to calculate next main renovation date
const calculateNextRenovationDate = (currentRenovationDate) => {
  const nextDate = new Date(currentRenovationDate);
  nextDate.setFullYear(nextDate.getFullYear() + 1);
  return nextDate.toISOString().split('T')[0];
};

// Function to calculate expiration date of overtime days (2 months after renovation)
const calculateExpirationDate = (renovationDate) => {
  const expirationDate = new Date(renovationDate);
  expirationDate.setMonth(expirationDate.getMonth() + 2);
  return expirationDate.toISOString().split('T')[0];
};

// Function to calculate vacation days based on seniority by Mexican Law
const calculateVacationDays = (dateOfEntry, referenceDate) => {
  if (!dateOfEntry) return 0;

  // Use provided reference date or today
  const todayStr = referenceDate ? referenceDate : new Date().toISOString().split('T')[0];
  const todayDate = new Date(todayStr);

  const entryStr = dateOfEntry.split('T')[0];
  const entryDate = new Date(entryStr);
  let years = todayDate.getFullYear() - entryDate.getFullYear();
  const anniversaryDate = new Date(
    todayDate.getFullYear(),
    entryDate.getMonth(),
    entryDate.getDate()
  );
  if (todayDate < anniversaryDate) {
    years--;
  }

  if (years <= 0) return 0;
  else if (years === 1) return 12;
  else if (years === 2) return 14;
  else if (years === 3) return 16;
  else if (years === 4) return 18;
  else if (years === 5) return 20;
  else if (years >= 6 && years <= 10) return 22;
  else if (years >= 11 && years <= 15) return 24;
  else if (years >= 16 && years <= 20) return 26;
  else if (years >= 21 && years <= 25) return 28;
  else if (years >= 26 && years <= 30) return 30;
  else return 32;
};

// Main update function that handles all checks and updates
export const checkAndUpdateEmployeeData = async (employees) => {
  const today = getCurrentDate();
  console.log('\n========== DATE INFORMATION ==========');
  console.log('Date being used for comparisons:', today);
  console.log('Test mode:', USE_TEST_DATE ? 'YES' : 'NO');
  console.log('Timezone:', TIMEZONE);
  console.log('=====================================\n');

  const updates = [];
  const errors = [];
  const updatedEmployees = [...employees];

  try {
    for (let i = 0; i < employees.length; i++) {
      const employee = employees[i];
      console.log(`\n========== Processing Employee ID: ${employee.id} ==========`);
      console.log(`Employee Name: ${employee.full_name}`);

      // Step #1 & #2: Check/Create overtime vacation days record
      console.log('\nStep #1: Checking for overtime vacation days record...');

      let { data: overtimeData, error: overtimeError } = await supabase
        .from('overtime_vacation_days')
        .select('*')
        .eq('employee_id', employee.id)
        .single();

      if (overtimeError && overtimeError.code === 'PGRST116') {
        console.log('Step #2: No overtime record found. Creating new record...');
        const { data: newOvertimeData, error: createError } = await supabase
          .from('overtime_vacation_days')
          .insert([{
            employee_id: employee.id,
            days_remaining: null,
            expiration_date: null
          }])
          .select()
          .single();

        if (createError) throw createError;
        overtimeData = newOvertimeData;
        console.log('✓ New overtime record created successfully');
      } else if (overtimeError) {
        throw overtimeError;
      } else {
        console.log('✓ Existing overtime record found');
      }

      // Function to process one complete check cycle
      const processCheckCycle = async (currentEmployee, currentOvertimeData) => {
        console.log('\n=== Starting New Check Cycle ===');
        console.log('Current Employee State:', {
          id: currentEmployee.id,
          name: currentEmployee.full_name,
          renovation_day: currentEmployee.renovation_day,
          remaining_days: currentEmployee.remaining_days,
          days_taken: currentEmployee.days_taken
        });
        console.log('Current Overtime State:', {
          days_remaining: currentOvertimeData.days_remaining,
          expiration_date: currentOvertimeData.expiration_date
        });

        let employeeUpdated = false;

        // Check expiration date (Steps #3-5)
        console.log('\nChecking expiration date...');
        if (currentOvertimeData.expiration_date && currentOvertimeData.expiration_date <= today) {
          console.log('Step #4-5: Expiration date has passed. Updating records...');
          console.log('Current state:', {
            employeeRemainingDays: currentEmployee.remaining_days,
            overtimeDaysRemaining: currentOvertimeData.days_remaining,
            expirationDate: currentOvertimeData.expiration_date
          });

          // Calculate new remaining days by subtracting expired overtime days
          const newRemainingDays = Math.max(
            (currentEmployee.remaining_days || 0) - (currentOvertimeData.days_remaining || 0),
            0
          );
          console.log('New remaining days will be:', newRemainingDays);

          // Queue update for employee remaining days
          updates.push({
            type: 'employee_overtime_expiration',
            operation: supabase
              .from('employees')
              .update({ remaining_days: newRemainingDays })
              .eq('id', currentEmployee.id)
          });

          // Queue update to reset overtime record
          updates.push({
            type: 'overtime_reset',
            operation: supabase
              .from('overtime_vacation_days')
              .update({
                days_remaining: null,
                expiration_date: null
              })
              .eq('employee_id', currentEmployee.id)
          });

          // Update the local copy of the data
          currentEmployee = {
            ...currentEmployee,
            remaining_days: newRemainingDays
          };

          currentOvertimeData = {
            ...currentOvertimeData,
            days_remaining: null,
            expiration_date: null
          };

          console.log('✓ Updates queued for expired overtime days');
          console.log('New state:', {
            employeeRemainingDays: newRemainingDays,
            overtimeDaysRemaining: null,
            expirationDate: null
          });

          employeeUpdated = true;
        } else {
          console.log('No expired overtime days to process');
          console.log('Current overtime state:', {
            daysRemaining: currentOvertimeData.days_remaining,
            expirationDate: currentOvertimeData.expiration_date
          });
        }

        // Check renovation day (Steps #6-13)
        console.log('\nChecking renovation day...');
        if (currentEmployee.renovation_day <= today) {
          console.log('⚠️ Renovation day has arrived or passed!');
          console.log('Renovation day:', currentEmployee.renovation_day);
          console.log('Today:', today);

          // Calculate vacation days based on seniority at renovation time
          const vacationDays = calculateVacationDays(
            currentEmployee.date_of_entry,
            currentEmployee.renovation_day
          );
          console.log('Calculated vacation days based on seniority at renovation time:', {
            dateOfEntry: currentEmployee.date_of_entry,
            renovationDate: currentEmployee.renovation_day,
            vacationDays: vacationDays
          });

          // Handle remaining days transfer first (Steps #8-10)
          if (currentEmployee.remaining_days >= 1) {
            console.log('\nTransferring remaining days to overtime:', currentEmployee.remaining_days);
            const newExpirationDate = calculateExpirationDate(currentEmployee.renovation_day);
            console.log('Calculated expiration date:', newExpirationDate);

            // Add overtime update first
            updates.push({
              type: 'overtime_transfer',
              operation: supabase
                .from('overtime_vacation_days')
                .update({
                  days_remaining: currentEmployee.remaining_days,
                  expiration_date: newExpirationDate
                })
                .eq('employee_id', currentEmployee.id)
            });

            currentOvertimeData = {
              ...currentOvertimeData,
              days_remaining: currentEmployee.remaining_days,
              expiration_date: newExpirationDate
            };
            console.log('Updated overtime data:', currentOvertimeData);
          } else {
            console.log('No remaining days to transfer to overtime');

            // Only reset overtime if we're not transferring days
            console.log('\nResetting overtime vacation days...');
            updates.push({
              type: 'overtime_reset',
              operation: supabase
                .from('overtime_vacation_days')
                .update({
                  days_remaining: null,
                  expiration_date: null
                })
                .eq('employee_id', currentEmployee.id)
            });
          }

          // Update employee vacation days (Steps #11-13)
          const nextRenovationDay = calculateNextRenovationDate(currentEmployee.renovation_day);
          const totalRemainingDays = Math.max(vacationDays + (currentEmployee.remaining_days || 0), 0);
          

          console.log('\nUpdating employee record...');
          console.log('Next renovation day:', nextRenovationDay);
          console.log('Calculation details:', {
            base_vacation_days: vacationDays,
            current_remaining_days: currentEmployee.remaining_days,
            //adjusted_vacation_days: adjustedVacationDays,
            overtime_days: currentOvertimeData.days_remaining,
            final_total: totalRemainingDays
          });
          console.log('Changes to apply:', {
            current_remaining_days: currentEmployee.remaining_days,
            new_vacation_days: vacationDays,
            overtime_days: currentOvertimeData.days_remaining,
            total_remaining_days: totalRemainingDays,
            days_taken: 0,
            renovation_day: nextRenovationDay
          });

          updates.push({
            type: 'employee_update',
            operation: supabase
              .from('employees')
              .update({
                remaining_days: totalRemainingDays,
                days_taken: 0,
                renovation_day: nextRenovationDay
              })
              .eq('id', currentEmployee.id)
          });

          currentEmployee = {
            ...currentEmployee,
            remaining_days: totalRemainingDays,
            days_taken: 0,
            renovation_day: nextRenovationDay
          };
          employeeUpdated = true;
          console.log('✓ Renovation day processing complete');
        } else {
          console.log('Renovation day has not arrived yet');
        }

        // If any updates were made, recursively check again
        if (employeeUpdated) {
          console.log('\n⟲ Changes were made - starting another check cycle...');
          return await processCheckCycle(currentEmployee, currentOvertimeData);
        }

        console.log('\n✓ Check cycle complete - no more updates needed');
        return { currentEmployee, currentOvertimeData };
      };

      // Start the initial check cycle
      const { currentEmployee } = await processCheckCycle(employee, overtimeData);
      updatedEmployees[i] = currentEmployee;

      console.log('\nStep #14: Process completed for this employee');
      console.log('================================================\n');
    }

    // Execute updates sequentially
    if (updates.length > 0) {
      console.log(`\n📝 Executing ${updates.length} queued updates sequentially...`);

      try {
        for (let i = 0; i < updates.length; i++) {
          const update = updates[i];
          console.log(`Executing update #${i + 1} (${update.type})...`);

          const result = await update.operation;

          if (result.error) {
            console.error(`❌ Update #${i + 1} (${update.type}) failed:`, result.error);
            errors.push(result.error);
          } else {
            console.log(`✓ Update #${i + 1} (${update.type}) completed successfully`);
          }
        }

        if (errors.length === 0) {
          console.log('✓ All updates completed successfully');

          console.log('\nFetching fresh data for affected employees...');
          const updatedIds = [...new Set(employees.map(emp => emp.id))];
          const { data: refreshedEmployees, error: refreshError } = await supabase
            .from('employees')
            .select('*')
            .in('id', updatedIds)
            .order('id', { ascending: true });

          if (!refreshError && refreshedEmployees) {
            console.log('✓ Fresh data retrieved successfully');
            refreshedEmployees.forEach(refreshedEmp => {
              const index = updatedEmployees.findIndex(emp => emp.id === refreshedEmp.id);
              if (index !== -1) {
                console.log(`Updating local data for employee ${refreshedEmp.id}:`, refreshedEmp);
                updatedEmployees[index] = refreshedEmp;
              }
            });
          } else if (refreshError) {
            console.error('❌ Error fetching fresh data:', refreshError);
          }
        }
      } catch (error) {
        console.error('❌ Error executing updates:', error);
        errors.push(error);
      }
    } else {
      console.log('\n✓ No updates were needed');
    }

    return {
      success: errors.length === 0,
      errors: errors.length > 0 ? errors : null,
      testDate: USE_TEST_DATE ? today : null,
      updatedEmployees: updatedEmployees
    };

  } catch (error) {
    console.error('Error during process:', error);
    return {
      success: false,
      errors: [error],
      testDate: USE_TEST_DATE ? today : null,
      updatedEmployees: employees
    };
  }
};